import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import {
    TripExtraRequest,
    TripExtraResponse,
    TripExtrasClient
} from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import {Equal, handleError, showNotFoundErrorNotification,} from 'src/utility/utils';
import { TripExtrasDataModel } from 'src/views/fishingTrip/Import/DataTypes';
import {getAllWorkers, selectWorker} from '@store/masterData/workerSlice';
import {getAllPositions, selectPosition} from '@store/masterData/positionSlice';
export interface TripExtra extends TripExtraResponse { }

export interface TripExtraModel extends TripExtraRequest {
    tripExtraId: number;
}

export interface State {
    isLoading: boolean;
    error: boolean;
    tripExtraList: TripExtra[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    tripExtraList: []
};

const tripExtraSlice = createSlice({
    name: 'tripExtra',
    initialState,
    reducers: {
        isLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getTripExtraListSuccess: (state, action) => {
            state.tripExtraList = action.payload;
            state.isLoading = false;
        },
        addNewTripExtraSuccess: (state, action: PayloadAction<TripExtra>) => {
            const newItem = action.payload;
            state.tripExtraList = [newItem, ...state.tripExtraList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateTripExtraSuccess: (state, action: PayloadAction<TripExtra>) => {
            const updatedItem = action.payload;
            const index = state.tripExtraList.findIndex(item => item.tripExtraId === updatedItem.tripExtraId);
            state.tripExtraList = [...state.tripExtraList.slice(0, index), updatedItem, ...state.tripExtraList.slice(index + 1)];
            state.isLoading = false;
        },
        deleteTripExtraSuccess: (state, action: PayloadAction<number>) => {
            const deleteId = action.payload;
            state.tripExtraList = state.tripExtraList.filter(item => item.tripExtraId !== deleteId);
            state.isLoading = false;
        },
        addCeBonusesSuccess: (state, action) => {
            state.tripExtraList = action.payload;
            toast.success(stringResources.FISHING_TRIP.POPULATE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default tripExtraSlice.reducer

// Actions
export const {
    isLoading,
    hasError,
    getTripExtraListSuccess,
} = tripExtraSlice.actions;

// for useSelector
export const selectTripExtra = (state: RootState) => state.tripExtra as State;

// ----------------------------------------------------------------------
export function getAllTripExtra(fishingTripId: number) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            const client = new TripExtrasClient(undefined, axios);
            const response = await client.findByFishingTripId(fishingTripId);
            dispatch(tripExtraSlice.actions.getTripExtraListSuccess(response));
            return response;
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createTripExtra(tripExtra: TripExtraModel) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            const { tripExtraId, ...payload } = tripExtra;
            const request = payload as TripExtraRequest;
            const client = new TripExtrasClient(undefined, axios);
            const response = await client.create(request);
            dispatch(tripExtraSlice.actions.addNewTripExtraSuccess(response));
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateTripExtra(tripExtra: TripExtraModel, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            const { tripExtraId, ...payload } = tripExtra;
            const request = payload as TripExtraRequest;
            const client = new TripExtrasClient(undefined, axios);
            const response = await client.edit(tripExtraId, request);
            dispatch(tripExtraSlice.actions.updateTripExtraSuccess(response));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function deleteTripExtra(tripExtraId: number, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            const client = new TripExtrasClient(undefined, axios);
            await client.delete(tripExtraId);
            dispatch(tripExtraSlice.actions.deleteTripExtraSuccess(tripExtraId));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function addCeBonuses(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            const client = new TripExtrasClient(undefined, axios);
            var response = await client.addCeBonuses(fishingTripNumber);
            dispatch(tripExtraSlice.actions.addCeBonusesSuccess(response));
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createTripExtraBatch(fishingTripId: number, data: TripExtrasDataModel[]) {
    return async (dispatch, getState) => {
        dispatch(tripExtraSlice.actions.isLoading());
        try {
            await dispatch(getAllWorkers());
            await dispatch(getAllPositions());

            const { workerList } = selectWorker(getState());
            const { positionList } = selectPosition(getState());
            const notFoundItems: string[] = [];
            
            const client = new TripExtrasClient(undefined, axios);
            const request: TripExtraRequest[] = [];
            for (const d of data) {
                const workerId = workerList.find(w => w.externalId === d.PGId)?.workerId;
                if (!workerId) {
                    notFoundItems.push(d.PGId ?? 'null');
                    continue;
                }

                const positionId = positionList.find(p => Equal(p.positionCode, d.PositionCode))?.positionId;
                if (!positionId) {
                    notFoundItems.push(d.PositionCode ?? 'null');
                    continue;
                }
                 request.push({
                     fishingTripId: fishingTripId,
                     workerId: workerId,
                     positionId: positionId,
                     category: d.Category,
                     description: d.Description,
                     calculationDescription: d.CalculationDescription,
                     quantity: d.Quantity,
                     rate: d.Rate
                 });
            }
            if (notFoundItems.length > 0) {
                showNotFoundErrorNotification(notFoundItems.join(','));
            }
            const response = await client.createBatch(request);
            dispatch(tripExtraSlice.actions.getTripExtraListSuccess(response));
        } catch (error) {
            dispatch(tripExtraSlice.actions.hasError(true));
            handleError(error);
        }
    };
}
